import React, { useState } from "react";
import SectionCard from "./SectionCard";
import { Globe, Flag } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Card } from "components/ui/card";

const TopRankingCard = ({
  data,
  showAll = false,
  isLoading,
  error,
  isHidden,
  onRetry,
}) => {
  const [sortBy, setSortBy] = useState("rank"); // "rank" | "h_index" | "publications"
  
  const sortedData = [...(data || [])].sort((a, b) => {
    if (sortBy === "rank") return (a.rank || 0) - (b.rank || 0);
    if (sortBy === "h_index") return (b.h_index || 0) - (a.h_index || 0);
    if (sortBy === "publications") return (b.publication_count || 0) - (a.publication_count || 0);
    return 0;
  });

  const displayData = showAll ? sortedData : sortedData.slice(0, 15);

  return (
    <SectionCard
      title="Top Ranking Subjects"
      isLoading={isLoading}
      error={error}
      isHidden={isHidden}
      isEmpty={!data || data.length === 0}
      onRetry={onRetry}
      headerEnd={
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[140px] h-8 text-xs">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="rank" className="text-xs">Top Ranked</SelectItem>
            <SelectItem value="h_index" className="text-xs">Highest h-index</SelectItem>
            <SelectItem value="publications" className="text-xs">Most Pubs</SelectItem>
          </SelectContent>
        </Select>
      }
    >
      <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 pt-2 ${showAll ? "max-h-[520px] overflow-y-auto pr-2" : ""}`}>
        {displayData.map((item, idx) => {
          const rank = item.rank || idx + 1;
          const scope = item.scope;
          const isGlobal = scope && (scope.toLowerCase() === "global" || scope.toLowerCase() === "world");

          return (
            <Card key={item.id || idx} className="hover:border-explore/50 hover:shadow-sm transition-all group border-border/40 bg-card">
              <div className="p-3.5 flex flex-col h-full gap-2">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="text-[15px] font-bold leading-tight capitalize text-foreground group-hover:text-explore transition-colors line-clamp-2" title={item.keyword}>
                    {item.keyword}
                  </h4>
                  <div className={`shrink-0 px-1.5 py-0.5 flex items-center justify-center rounded text-[10px] font-bold transition-colors ${rank <= 3 ? "bg-explore/10 text-explore" : "bg-muted/60 text-muted-foreground group-hover:bg-explore/5 group-hover:text-explore/80"}`}>
                    #{rank}
                  </div>
                </div>
                
                <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  <span>H-Index <strong className="text-foreground/90 ml-0.5">{item.h_index}</strong></span>
                  <div className="w-1 h-1 rounded-full bg-border"></div>
                  <span>Pubs <strong className="text-foreground/90 ml-0.5">{item.publication_count?.toLocaleString() || "-"}</strong></span>
                  
                  {scope && (
                    <>
                      <div className="w-1 h-1 rounded-full bg-border"></div>
                      <span className={`flex items-center gap-1 ${isGlobal ? "text-explore/80" : ""}`}>
                        {isGlobal ? <Globe className="w-2.5 h-2.5" /> : <Flag className="w-2.5 h-2.5" />}
                        {scope}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </SectionCard>
  );
};

export default TopRankingCard;
